// Къде стои поповърът на маса върху платното — чиста геометрия без React
// (тестове: popoverPlacement.test.ts). Всичко е в пиксели на видимото платно
// (`StageLayout.boxOf`), затова работи при всеки зуум и отместване.
//
// Поповърът е до масата — вдясно, вляво, под или над нея. Когато показва
// резервация на няколко маси, другите ѝ маси остават видими: пробват се
// страните, после плъзгане покрай масата и накрая отместване отвъд пречката;
// печели първото място, което не покрива нито една от тях и е изцяло в
// платното. Няма ли такова — най-малко покритото, избутано в платното (на
// тясно платно може да застъпи и самата маса). Чужди маси може да се покрият.

import type { Reservation } from "../../../../api/reservations";
import { DESIGN_UNIT } from "../../../../components/restaurant-schema/geometry";
import type { ElementBox } from "../../../../components/restaurant-schema/stageLayout";
import type { TableState } from "../../logic/tableStatus";

/** Правоъгълник в пиксели на платното. */
export type PixelRect = Pick<ElementBox, "left" | "top" | "right" | "bottom">;

export interface PopoverPosition {
  left: number;
  top: number;
}

// Отстоянието от масата.
const GAP = 12;
// Отстоянието от ръба на платното.
const EDGE_MARGIN = 8;
// Отстоянието от масите, които остават видими (сянката да не ги застъпва).
const CLEARANCE = 8;
// Вдясно/вляво: в горната половина на платното поповърът започва малко над
// горния ръб на масата, в долната — свършва малко под долния.
const EDGE_ALLOWANCE = 24;
// Плъзнат покрай масата, поповърът стои срещу нея поне с толкова — иначе не
// изглежда неин.
const MIN_ATTACH = 16;
// Под квадратен пиксел застъпване/излизане е грешка от закръгляне.
const AREA_TOLERANCE = 1;

type Side = "right" | "left" | "below" | "above";

interface PlacementInput {
  // Масата, до която е закотвен поповърът.
  anchor: PixelRect;
  // Масите, които остават видими (другите маси на показаната резервация).
  keepVisible: PixelRect[];
  // Размерът на поповъра.
  size: { width: number; height: number };
  // Размерът на видимото платно.
  canvas: { width: number; height: number };
}

/**
 * Едно място за поповъра: `stage` — естественото място на страната (0),
 * плъзнато покрай масата (1) или отместено отвъд пречка (2); `displacement` —
 * колко пиксела е от естественото място. Пробват се по `stage`, после по
 * `displacement`, после по реда на страните.
 */
interface Candidate {
  position: PopoverPosition;
  stage: 0 | 1 | 2;
  displacement: number;
  sideOrder: number;
}

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), Math.max(min, max));

const overlapArea = (first: PixelRect, second: PixelRect) =>
  Math.max(0, Math.min(first.right, second.right) - Math.max(first.left, second.left)) *
  Math.max(0, Math.min(first.bottom, second.bottom) - Math.max(first.top, second.top));

const areaOf = (rect: PixelRect) => (rect.right - rect.left) * (rect.bottom - rect.top);

const isHorizontalSide = (side: Side) => side === "right" || side === "left";

/**
 * Основната ос на страната (left за вдясно/вляво, top за под/над): плътно до
 * масата и отвъд всяка от пазените маси в посоката на страната.
 */
const primaryPositionsOf = (side: Side, { anchor, keepVisible, size }: PlacementInput) => {
  const beyondEach = (edgeOf: (rect: PixelRect) => number, isFurther: (value: number) => boolean) =>
    [...new Set(keepVisible.map(edgeOf))].filter(isFurther);
  switch (side) {
    case "right": {
      const base = anchor.right + GAP;
      return { base, beyond: beyondEach((rect) => rect.right + CLEARANCE, (value) => value > base) };
    }
    case "left": {
      const base = anchor.left - GAP - size.width;
      return { base, beyond: beyondEach((rect) => rect.left - CLEARANCE - size.width, (value) => value < base) };
    }
    case "below": {
      const base = anchor.bottom + GAP;
      return { base, beyond: beyondEach((rect) => rect.bottom + CLEARANCE, (value) => value > base) };
    }
    case "above": {
      const base = anchor.top - GAP - size.height;
      return { base, beyond: beyondEach((rect) => rect.top - CLEARANCE - size.height, (value) => value < base) };
    }
  }
};

/**
 * Второстепенната ос на страната (top за вдясно/вляво, left за под/над):
 * естественото място и плъзгане точно преди/след всяка пазена маса — в
 * платното и срещу масата (поне `MIN_ATTACH`); разминават ли се двете (масата
 * е извън видимото), само в платното.
 */
const slidePositionsOf = (side: Side, { anchor, keepVisible, size, canvas }: PlacementInput, inUpperHalf: boolean) => {
  const isHorizontal = isHorizontalSide(side);
  const [anchorStart, anchorEnd] = isHorizontal ? [anchor.top, anchor.bottom] : [anchor.left, anchor.right];
  const popoverLength = isHorizontal ? size.height : size.width;
  const canvasMax = Math.max(EDGE_MARGIN, (isHorizontal ? canvas.height : canvas.width) - EDGE_MARGIN - popoverLength);
  const attach = Math.min(MIN_ATTACH, (anchorEnd - anchorStart) / 2);
  const attachedMin = Math.max(EDGE_MARGIN, anchorStart - popoverLength + attach);
  const attachedMax = Math.min(canvasMax, anchorEnd - attach);
  const [min, max] = attachedMin <= attachedMax ? [attachedMin, attachedMax] : [EDGE_MARGIN, canvasMax];

  const natural = clamp(
    isHorizontal
      ? inUpperHalf
        ? anchor.top - EDGE_ALLOWANCE
        : anchor.bottom + EDGE_ALLOWANCE - size.height
      : (anchor.left + anchor.right) / 2 - size.width / 2,
    min,
    max,
  );
  const alongEach = keepVisible.flatMap((rect) =>
    isHorizontal
      ? [rect.bottom + CLEARANCE, rect.top - CLEARANCE - size.height]
      : [rect.right + CLEARANCE, rect.left - CLEARANCE - size.width],
  );
  return { natural, all: [...new Set([natural, ...alongEach.map((value) => clamp(value, min, max))])] };
};

/**
 * Мястото на поповъра с размер `size` до масата `anchor` в платното `canvas`,
 * без да покрива `keepVisible` (другите маси на показаната резервация).
 */
export const placePopover = (input: PlacementInput): PopoverPosition => {
  const { anchor, keepVisible, size, canvas } = input;
  const inUpperHalf = (anchor.top + anchor.bottom) / 2 < canvas.height / 2;
  // Встрани е обичайното място; под/над — първо към страната с повече място.
  const sides: Side[] = inUpperHalf ? ["right", "left", "below", "above"] : ["right", "left", "above", "below"];

  const candidates: Candidate[] = sides.flatMap((side, sideOrder) => {
    const primary = primaryPositionsOf(side, input);
    const slides = slidePositionsOf(side, input, inUpperHalf);
    return [primary.base, ...primary.beyond].flatMap((primaryPosition) =>
      slides.all.map((slidePosition): Candidate => {
        const push = Math.abs(primaryPosition - primary.base);
        const slideDistance = Math.abs(slidePosition - slides.natural);
        return {
          position: isHorizontalSide(side)
            ? { left: primaryPosition, top: slidePosition }
            : { left: slidePosition, top: primaryPosition },
          stage: push > 0 ? 2 : slideDistance > 0 ? 1 : 0,
          displacement: push + slideDistance,
          sideOrder,
        };
      }),
    );
  });
  candidates.sort(
    (first, second) =>
      first.stage - second.stage || first.displacement - second.displacement || first.sideOrder - second.sideOrder,
  );

  // Първото място изцяло в платното, без да покрива. Няма ли такова — мястото,
  // избутано в платното (поповърът се чете цял и на тясно платно на таблет),
  // което покрива най-малко от пазените маси и от самата маса.
  const visibleCanvas: PixelRect = {
    left: EDGE_MARGIN,
    top: EDGE_MARGIN,
    right: canvas.width - EDGE_MARGIN,
    bottom: canvas.height - EDGE_MARGIN,
  };
  const rectAt = (position: PopoverPosition): PixelRect => ({
    left: position.left,
    top: position.top,
    right: position.left + size.width,
    bottom: position.top + size.height,
  });
  const coveredBy = (rect: PixelRect) => keepVisible.reduce((sum, keep) => sum + overlapArea(rect, keep), 0);
  let best: { position: PopoverPosition; covered: number } | null = null;
  for (const { position } of candidates) {
    const rect = rectAt(position);
    const outside = Math.max(0, areaOf(rect) - overlapArea(rect, visibleCanvas));
    if (outside < AREA_TOLERANCE && coveredBy(rect) < AREA_TOLERANCE) return position;
    // По-висок/по-широк от платното — закотвен горе/вляво (`clamp`).
    const inCanvas = {
      left: clamp(position.left, EDGE_MARGIN, canvas.width - EDGE_MARGIN - size.width),
      top: clamp(position.top, EDGE_MARGIN, canvas.height - EDGE_MARGIN - size.height),
    };
    const inCanvasRect = rectAt(inCanvas);
    const covered = coveredBy(inCanvasRect) + overlapArea(inCanvasRect, anchor);
    if (!best || covered < best.covered - AREA_TOLERANCE) best = { position: inCanvas, covered };
  }
  return best?.position ?? { left: EDGE_MARGIN, top: EDGE_MARGIN };
};

/**
 * Пазената маса заедно с чипа „следваща резервация" над нея (`hasChip`) —
 * той е част от масата и поповърът не бива да го покрива. Огледало на чипа в
 * `TableView`: отстояние 4 над най-горния стол, шрифт 10 (поне 8px), отстъп 3
 * горе и долу и рамка 1px — в единици на дизайна × мащаба.
 */
export const keepVisibleBoxOf = (box: PixelRect, scale: number, hasChip: boolean): PixelRect => {
  if (!hasChip) return box;
  const designPixel = DESIGN_UNIT * scale;
  const chipReach = (4 + 2 * 3) * designPixel + Math.max(8, 10 * designPixel) + 2;
  return { ...box, top: box.top - chipReach };
};

/**
 * Масите, които поповърът на маса не бива да покрива: на резервациите, които
 * той показва (огледало на `TablePopoverBody`) — резервираната/настанената,
 * предстоящата в този час, следващата в бележката — и на избраната (отказана
 * от „Минали" с „Върни"). Без самата маса — до нея е закотвен.
 */
export const keepVisibleTableIdsOf = (
  tableId: number,
  state: TableState,
  selectedTableIds: ReadonlySet<number>,
): Set<number> => {
  const shown: (Reservation | null)[] =
    state.kind === "reserved"
      ? [state.reservation]
      : state.kind === "free"
        ? [state.next]
        : // Следващата не се показва до настанената (тя държи масата), нито
          // когато в този час има предстояща — тя заема мястото на бележката.
          [
            state.reservation,
            state.reservedMeanwhile,
            state.reservation?.status === "seated" || state.reservedMeanwhile ? null : state.next,
          ];
  const ids = new Set(selectedTableIds);
  for (const reservation of shown) reservation?.tableIds.forEach((id) => ids.add(id));
  ids.delete(tableId);
  return ids;
};
